import { gsap } from 'gsap'
import { createElement } from '@/_utilities/dom'

/**
 * The persistent 2D chrome layered over the 3D scene: the wordmark, the control
 * hint, the mute toggle, and the full-screen warp flash used during transitions.
 * Owns the shared overlay layer that the info card is mounted into as well.
 */
export class Overlay {
  public readonly layer: HTMLDivElement

  private readonly muteButton: HTMLButtonElement
  private readonly hintElement: HTMLParagraphElement
  private readonly flashElement: HTMLDivElement

  /**
   * @param {() => void} onToggleMute - Called when the mute control is pressed.
   * @param {() => void} onToggleView - Called when the list-view control is pressed.
   */
  public constructor(onToggleMute: () => void, onToggleView: () => void) {
    this.layer = createElement('div', { className: 'overlay-layer' })

    const wordmark = createElement('div', { className: 'overlay-wordmark' })
    wordmark.append(
      createElement('span', { className: 'overlay-wordmark-main', text: 'QuickCasa' }),
      createElement('span', { className: 'overlay-wordmark-sub', text: 'Ecosystem' }),
    )

    this.hintElement = createElement('p', {
      className: 'overlay-hint',
      text: 'Hover a product to learn more. Click to open. Scroll to zoom.',
    })

    const viewToggleButton = createElement('button', {
      className: 'overlay-control overlay-view-toggle',
      html: '<span aria-hidden="true">☰</span> List view',
      attributes: { 'aria-label': 'Open the list view' },
    })
    viewToggleButton.addEventListener('click', onToggleView)

    this.muteButton = createElement('button', {
      className: 'overlay-control overlay-mute',
      text: 'Sound on',
      attributes: { 'aria-label': 'Toggle sound' },
    })
    this.muteButton.addEventListener('click', onToggleMute)

    const controls = createElement('div', { className: 'overlay-controls' })
    controls.append(viewToggleButton, this.muteButton)

    this.flashElement = createElement('div', { className: 'overlay-flash' })

    this.layer.append(wordmark, this.hintElement, controls, this.flashElement)
  }

  /**
   * Reflect the current mute state on the mute control.
   *
   * @param {boolean} isMuted - True when audio is muted.
   * @returns {void}
   */
  public setMuted(isMuted: boolean): void {
    this.muteButton.textContent = isMuted ? 'Sound off' : 'Sound on'
    this.muteButton.classList.toggle('is-muted', isMuted)
  }

  /**
   * Update the control hint text (used to tailor the hint for touch devices).
   *
   * @param {string} text - The hint to display.
   * @returns {void}
   */
  public setHint(text: string): void {
    this.hintElement.textContent = text
  }

  /**
   * Flash the screen in a colour to punctuate a warp transition.
   *
   * @param {string} colour - The flash colour.
   * @returns {void}
   */
  public triggerWarpFlash(colour: string): void {
    this.flashElement.style.background = `radial-gradient(circle at center, ${colour} 0%, transparent 70%)`
    gsap.fromTo(
      this.flashElement,
      { opacity: 0 },
      { opacity: 0.85, duration: 0.5, ease: 'power2.in', yoyo: true, repeat: 1 },
    )
  }

  /**
   * Fade the entire overlay in once the scene takes over from the loader.
   *
   * @returns {void}
   */
  public reveal(): void {
    gsap.fromTo(this.layer, { opacity: 0 }, { opacity: 1, duration: 1.2, ease: 'power2.out' })
  }
}
